'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontSelector } from './FontSelector';
import './CompoundFontSelector.css';
/**
 * CompoundFontSelector — Combined font selector that exposes primary, fallback and generic font fields as one compound value.
 *
 * @param {string} [props.id] - Base id used for the internal font inputs.
 * @param {string} [props.name] - Base name used for the internal font inputs.
 * @param {string} [props.label] - Label displayed above the compound selector.
 * @param {boolean} [props.required] - Whether selecting a primary font is required.
 * @param {string} [props.value] - Current combined font value (comma-separated stack).
 * @param {function} [props.onChange] - Callback invoked with the combined font stack when any part changes.
 */
CompoundFontSelector.propTypes = {
    /** Base id for internal inputs */
    id: PropTypes.string.isRequired,
    /** Base name for internal inputs */
    name: PropTypes.string.isRequired,
    /** Display label for the control */
    label: PropTypes.string.isRequired,
    /** Required flag for the primary font */
    required: PropTypes.bool,
    /** Combined font stack value */
    value: PropTypes.string,
    /** onChange callback receiving combined font string */
    onChange: PropTypes.func,
};
export function CompoundFontSelector(props) {
    const { id, name, label, required = false, value = '', onChange } = props;
    // Parse the compound value into primary, fallback, generic parts
    const parseCompoundValue = (val) => {
        const parts = val.split(',').map(s => s.trim());
        return {
            primary: parts[0] || '',
            fallback: parts[1] || '',
            generic: parts[2] || ''
        };
    };
    const [fonts, setFonts] = useState(parseCompoundValue(value || ''));
    // Update fonts when value changes externally
    useEffect(() => {
        setFonts(parseCompoundValue(value || ''));
    }, [value]);
    // Handle changes from individual font selectors
    const handleFontChange = (type, newValue) => {
        const updatedFonts = { ...fonts, [type]: newValue };
        setFonts(updatedFonts);
        const combinedValue = [updatedFonts.primary, updatedFonts.fallback, updatedFonts.generic]
            .filter(Boolean)
            .join(', ');
        onChange?.(combinedValue);
    };
    return (_jsxs("div", { className: "compound-font-selector", children: [_jsx("label", { children: label }), _jsxs("div", { className: "compound-container", children: [_jsx(FontSelector, { id: `${id}-primary`, name: `${name}-primary`, label: "Primary Font", fontType: "google", required: !!required, placeholder: "Select Google Font", value: fonts.primary, onChange: (val) => handleFontChange('primary', val) }), _jsx(FontSelector, { id: `${id}-fallback`, name: `${name}-fallback`, label: "Fallback Font", fontType: "websafe", required: false, placeholder: "Select web-safe font", value: fonts.fallback, onChange: (val) => handleFontChange('fallback', val) }), _jsx(FontSelector, { id: `${id}-generic`, name: `${name}-generic`, label: "Generic Family", fontType: "generic", required: false, placeholder: "Select generic family", value: fonts.generic, onChange: (val) => handleFontChange('generic', val) })] })] }));
}
/**
 * CompoundFontSelector — Combined font selector that exposes primary, fallback and generic font fields as one compound value.
 *
 * @param {string} [props.id] - Base id used for the internal font inputs.
 * @param {string} [props.name] - Base name used for the internal font inputs.
 * @param {string} [props.label] - Label displayed above the compound selector.
 * @param {boolean} [props.required] - Whether selecting a primary font is required.
 * @param {string} [props.value] - Current combined font value (comma-separated stack).
 * @param {function} [props.onChange] - Callback invoked with the combined font stack when any part changes.
 */
CompoundFontSelector.propTypes = {
    /** Base id for internal inputs */
    id: PropTypes.string.isRequired,
    /** Base name for internal inputs */
    name: PropTypes.string.isRequired,
    /** Display label for the control */
    label: PropTypes.string.isRequired,
    /** Required flag for the primary font */
    required: PropTypes.bool,
    /** Combined font stack value */
    value: PropTypes.string,
    /** onChange callback receiving combined font string */
    onChange: PropTypes.func,
};
